import axios from 'axios';
import * as types from '../mutation-types';
import type {Connection} from '@/types';
import type {StoreLoaders} from '@/types/common';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import {push} from 'notivue';

export interface BouncieVehicle {
    uuid: string;
    active?: boolean | number;
    display_name?: string | null;
    imei?: string | null;
    make?: string | null;
    model?: string | null;
    year?: string | number | null;
    matched_v_id?: string | null;
    vin: string;
}

export interface BouncieConnection extends Omit<Connection, 'uuid'> {
    uuid: string;
    name?: string | null;
    vehicles?: BouncieVehicle[];
}

/** A vehicle with the account it belongs to, as the vehicle views use it. */
export interface BouncieAccountVehicle extends BouncieVehicle {
    bouncie_uuid: string;
    email: string;
    name?: string | null;
}

export interface BouncieState {
    init: boolean;
    loading: StoreLoaders;
    connections: BouncieConnection[];
}

const state: BouncieState = {
    init: false,

    loading: {
        toggle: false,
        delete: false,
        vehicles: false,
        vehicleState: false,
        trips: false,
    },

    connections: [],
};

const getters = {
    init: (state: BouncieState) => state.init,
    loading: (state: BouncieState) => state.loading,
    connections: (state: BouncieState) => state.connections,
    /** Every vehicle across every connected account. */
    vehicles: (state: BouncieState): BouncieAccountVehicle[] => state.connections.flatMap((connection) =>
        (connection.vehicles ?? []).map((vehicle) => ({
            ...vehicle,
            bouncie_uuid: connection.uuid,
            email: connection.email,
            name: connection.name,
        }))
    ),
};

const mutations = {
    [types.SET_BOUNCIE_INIT](state: BouncieState, init: boolean) {
        state.init = init;
    },
    [types.SET_BOUNCIE_LOADING](state: BouncieState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_BOUNCIE_CONNECTIONS](state: BouncieState, connections: BouncieConnection[]) {
        state.connections = connections;
    },
    /** Replaces one account in place, so refreshing it doesn't drop the others. */
    UPSERT_BOUNCIE_CONNECTION(state: BouncieState, connection: BouncieConnection) {
        const index = state.connections.findIndex((c) => c.uuid === connection.uuid);

        if (index === -1) {
            state.connections.push(connection);
        } else {
            state.connections.splice(index, 1, connection);
        }
    },
};

const actions = {
    /**
     * Asks Bouncie for the account's current vehicles (with live stats) and
     * stores the synced account.
     */
    async fetchVehicles({commit}, bouncie: Pick<Connection, 'uuid'>) {
        try {
            const responseData = await performApiRequest<{ bouncie: BouncieConnection; vehicles: any[] }>(
                commit,
                'SET_BOUNCIE_LOADING',
                'vehicles',
                () => axios.get(`/v1/connections/bouncie/${bouncie.uuid}/vehicles`)
            );

            if (responseData?.bouncie) {
                commit('UPSERT_BOUNCIE_CONNECTION', responseData.bouncie);
            }

            return responseData;
        } catch (error: any) {
            push.error({message: error?.response?.data?.message ?? 'Could not refresh Bouncie vehicles.'});
        }
    },

    /** Refreshes every active account. */
    async fetchAllVehicles({dispatch, state}) {
        const results = await Promise.all(
            state.connections
                .filter((connection: BouncieConnection) => connection.active)
                .map((connection: BouncieConnection) => dispatch('fetchVehicles', connection))
        );

        return results.filter(Boolean);
    },

    /**
     * Fetch Bouncie vehicle state
     */
    async getVehicleState({commit}, vehicle: BouncieAccountVehicle) {
        try {
            const responseData = await performApiRequest<{ state: any }>(commit, 'SET_BOUNCIE_LOADING', 'vehicleState', () => axios.get(`/v1/connections/bouncie/${vehicle.bouncie_uuid}/vehicles/${vehicle.uuid}`));

            return responseData.state;
        } catch (error: any) {
            push.error({message: error?.response?.data?.message ?? 'Could not reach this vehicle.'});
        }
    },

    async getVehicleTrips({commit}, data: { vehicle: BouncieAccountVehicle }) {
        try {
            const responseData = await performApiRequest<{ trips: any[] }>(commit, 'SET_BOUNCIE_LOADING', 'trips', () => axios.get(`/v1/connections/bouncie/${data.vehicle.bouncie_uuid}/trips/${data.vehicle.uuid}`));

            return responseData.trips ?? [];
        } catch (error: any) {
            push.error({message: error?.response?.data?.message ?? 'Could not load trips.'});
        }
    },

    /**
     * Turn a Bouncie connection on or off
     */
    async toggleActive({commit}, bouncie: Connection) {
        try {
            const responseData = await performApiRequest<any>(commit, 'SET_BOUNCIE_LOADING', 'toggle', () => axios.put(`/v1/connections/bouncie/${bouncie.uuid}/toggle`));
            push.success(bouncie.active ? 'Bouncie connection turned on.' : 'Bouncie connection turned off.');
            commit('SET_BOUNCIE_CONNECTIONS', responseData?.company?.connections?.bouncie || []);

            return true;
        } catch {
            push.error('Could not update the Bouncie connection.');

            return false;
        }
    },

    /**
     * Delete Bouncie connection
     */
    async deleteConnection({commit}, bouncie: Connection) {
        try {
            const responseData = await performApiRequest<any>(commit, 'SET_BOUNCIE_LOADING', 'delete', () => axios.delete(`/v1/connections/bouncie/${bouncie.uuid}`));
            push.success('Bouncie connection removed.');
            commit(types.SET_BOUNCIE_CONNECTIONS, responseData?.company?.connections?.bouncie || []);

            return true;
        } catch {
            push.error('Could not remove the Bouncie connection.');

            return false;
        }
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
